import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';

const NavFragment = ({
	topMenuItemIndex,
	setIsNavLink = () => {},
	fragmentId,
	fragmentUrl
}) => {
	const openMenus = useSelector((state) => state.menu.openMenus);
	const fullscreenMobileSubnavOpen = useSelector(
		(state) => state.menu.fullscreenMobileSubnavOpen
	);
	const isTopMenuItemOpen =
		openMenus?.includes(topMenuItemIndex) || fullscreenMobileSubnavOpen;
	const fragmentRef = useRef(null);
	const hasLoaded = useRef(false);
	const [loadedHtml, setLoadedHtml] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const cleanHtml = useMemo(
		() => (loadedHtml === null ? null : DOMPurify.sanitize(loadedHtml)),
		[loadedHtml]
	);
	setIsNavLink(false);

	useEffect(() => {
		// prevents unnecessary rendering of fragments
		if (!isTopMenuItemOpen || hasLoaded.current) {
			return undefined;
		}

		const controller = new AbortController();
		setIsLoading(true);

		// jQuery trigger used here intentionally: consumers receive data as a
		// second argument (event, data) — not achievable with native CustomEvent
		if (
			typeof window.jQuery === 'function' &&
			window.DDC?.modules?.lazyImage
		) {
			$(document).trigger('modulesRefresh', {
				modules: { lazyImage: window.DDC.modules.lazyImage }
			});
		}

		fetch(fragmentUrl, { signal: controller.signal })
			.then((response) => {
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				return response.text();
			})
			.then((html) => {
				// Only mark as loaded once the content has actually arrived, so
				// a slow or aborted request doesn't block a later retry.
				hasLoaded.current = true;
				setLoadedHtml(html);
				setIsLoading(false);
			})
			.catch((err) => {
				// hasLoaded stays false on failure/abort, so the next
				// menu-open will retry the fetch.
				if (err.name !== 'AbortError') {
					setIsLoading(false);
				}
			});

		return () => controller.abort();
	}, [isTopMenuItemOpen, fragmentId, fragmentUrl]);

	useEffect(() => {
		if (loadedHtml !== null && fragmentRef.current) {
			const ddcContent =
				fragmentRef.current.querySelector('.ddc-content');
			if (ddcContent) {
				ddcContent.dispatchEvent(
					new CustomEvent('ddc-mega-menu-nav-ajax-load-complete', {
						bubbles: true
					})
				);
			}
			document.dispatchEvent(new CustomEvent('modulesRefresh'));
		}
	}, [loadedHtml]);

	if (loadedHtml !== null) {
		return (
			<li role="presentation">
				<div
					ref={fragmentRef}
					className="ajax-navigation-element ajax-load-complete"
					data-fragment-id={fragmentId}
					data-fragment-url={fragmentUrl}
					style={{ visibility: 'visible' }}
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={{ __html: cleanHtml }}
				/>
			</li>
		);
	}

	return (
		<li role="presentation">
			{isLoading && (
				<span className="navigation-ajax-container loading">
					<i className="ddc-icon ddc-icon-specs-gear1 ddc-icon-size-xlarge" />{' '}
					Loading...
				</span>
			)}
			<div
				className="hide ajax-navigation-element"
				data-fragment-id={fragmentId}
				data-fragment-url={fragmentUrl}
			/>
		</li>
	);
};

export default NavFragment;

NavFragment.propTypes = {
	topMenuItemIndex: PropTypes.number.isRequired,
	setIsNavLink: PropTypes.func,
	fragmentId: PropTypes.string.isRequired,
	fragmentUrl: PropTypes.string.isRequired
};
